import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Popover, IconButton, Tooltip } from '@mui/material';
import { ReactComponent as InfoIcon } from '../assets/SVG/info-circle.svg';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

export interface TruncatedTextProps {
  /** The text to display with truncation */
  text: string | null | undefined;
  /** Title to show in the popover header */
  title?: string;
  /** Placeholder to show when text is empty */
  placeholder?: string;
  /** Maximum width for the popover content */
  popoverMaxWidth?: number;
  /** Position of the info icon when text is truncated */
  iconPosition?: 'left' | 'right';
}

/**
 * A reusable component that displays text with CSS-based truncation.
 * When text overflows, it shows an ellipsis (...) and an info icon.
 * Clicking the info icon opens a popover with the full text.
 * 
 * The truncation is based on available container space, not character count.
 * 
 * @example
 * <TruncatedText text="This is a very long text that might get truncated" />
 * 
 * @example
 * <TruncatedText text={description} placeholder="-" popoverMaxWidth={400} />
 */
const TruncatedText: React.FC<TruncatedTextProps> = ({ 
  text, 
  title,
  placeholder = '-',
  popoverMaxWidth = 300,
  iconPosition = 'left'
}) => {
  const { t: tBilling } = useTranslation('billing_config');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  
  // Check if text is truncated based on available space
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };
    
    checkTruncation();
    // Re-check on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);
  
  if (!text || typeof text !== 'string') return <Typography variant="body2">{placeholder}</Typography>;
  
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const infoIcon = (
    <Tooltip title={tBilling('Advance.click_to_view_full_text', { defaultValue: 'Click to view full text' })} arrow>
      <Box
        component="span"
        onClick={handlePopoverOpen}
        role="button"
        tabIndex={0}
        aria-label={tBilling('Advance.show_full_text', { defaultValue: 'Show full text' })}
        aria-describedby={open ? 'truncated-text-popover' : undefined}
        aria-expanded={open}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePopoverOpen(e as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
        sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      >
        <InfoIcon style={{ cursor: 'pointer' }} />
      </Box>
    </Tooltip>
  );
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {isTruncated && iconPosition === 'left' && infoIcon}
      <Box
        component="span"
        ref={textRef}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {text}
      </Box>
      {isTruncated && (
        <>
          {iconPosition === 'right' && infoIcon}
          <Popover
            id="truncated-text-popover"
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            onClose={handlePopoverClose}
          >
            <Box sx={{ maxWidth: popoverMaxWidth }}>
              {title && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 1, 
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f7fafc'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {title}
                  </Typography>
                  <IconButton size="small" onClick={handlePopoverClose} sx={{ p: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {text}
                </Typography>
              </Box>
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default TruncatedText;
